import { X } from 'lucide-react';
import { useState } from 'react';

const MAX_BUTTONS = 3;
const MAX_BUTTON_TITLE_CHARS = 20;
const MAX_LIST_ROWS = 10;

const emptyButton = () => ({ id: `b${Math.random().toString(36).slice(2, 8)}`, title: '' });
const emptyRow = () => ({ id: `r${Math.random().toString(36).slice(2, 8)}`, title: '', description: '' });

const TITLES = { buttons: 'Quick-reply buttons', list: 'List menu', cta_url: 'Link button' };

const inputClass = 'w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none';

/**
 * Builds a buttons / list / link-button interactive send. Validation mirrors the backend's own
 * limits (routes/admin/whatsapp_inbox.py _clean_interactive) so a bad shape is caught here, not
 * round-tripped to the server just to be told no.
 * @param {{kind: 'buttons' | 'list' | 'cta_url', onSend: (payload: any) => Promise<{ok: boolean, code: string | null, message?: string}>, onCancel: () => void}} props
 */
const InteractiveComposer = ({ kind, onSend, onCancel }) => {
  const [body, setBody] = useState('');
  const [buttons, setButtons] = useState([emptyButton()]);
  const [buttonText, setButtonText] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const buildPayload = () => {
    const trimmedBody = body.trim();
    if (!trimmedBody) return { error: 'Write a message first' };
    if (kind === 'buttons') {
      const cleaned = buttons.map((b) => ({ id: b.id, title: b.title.trim() })).filter((b) => b.title);
      if (!cleaned.length || cleaned.length > MAX_BUTTONS) return { error: `Add 1-${MAX_BUTTONS} buttons` };
      if (cleaned.some((b) => b.title.length > MAX_BUTTON_TITLE_CHARS)) {
        return { error: `A button title can be at most ${MAX_BUTTON_TITLE_CHARS} characters` };
      }
      return { payload: { kind: 'buttons', body: trimmedBody, buttons: cleaned } };
    }
    if (kind === 'list') {
      const cleaned = rows
        .map((r) => ({ id: r.id, title: r.title.trim(), description: r.description.trim() || null }))
        .filter((r) => r.title);
      if (!cleaned.length || cleaned.length > MAX_LIST_ROWS) return { error: `Add 1-${MAX_LIST_ROWS} rows` };
      if (!buttonText.trim()) return { error: "Set the list button's label" };
      return {
        payload: {
          kind: 'list',
          body: trimmedBody,
          button_text: buttonText.trim(),
          sections: [{ title: 'Options', rows: cleaned }],
        },
      };
    }
    // cta_url
    if (!buttonText.trim()) return { error: 'Set the link button label' };
    if (!/^https:\/\//i.test(url.trim())) return { error: 'The link needs an https:// url' };
    return { payload: { kind: 'cta_url', body: trimmedBody, button_text: buttonText.trim(), url: url.trim() } };
  };

  const submit = async () => {
    const { payload, error: validationError } = buildPayload();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setSending(true);
    const result = await onSend(payload);
    setSending(false);
    if (result.ok) onCancel();
    else if (!result.code) setError(result.message || 'Could not send that message');
  };

  return (
    <div className="mb-2 rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-800">{TITLES[kind]}</p>
        <button type="button" aria-label="Close" onClick={onCancel} className="text-gray-500 hover:text-gray-800">
          <X size={16} />
        </button>
      </div>
      <textarea
        aria-label="Message body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        placeholder="Message body"
        className={`mb-2 resize-none ${inputClass}`}
      />
      {kind === 'buttons' && (
        <div className="space-y-1">
          {buttons.map((b, i) => (
            <div key={b.id} className="flex items-center gap-1">
              <input
                aria-label={`Button ${i + 1} label`}
                value={b.title}
                maxLength={MAX_BUTTON_TITLE_CHARS}
                onChange={(e) =>
                  setButtons((prev) => prev.map((x) => (x.id === b.id ? { ...x, title: e.target.value } : x)))
                }
                className={inputClass}
              />
              {buttons.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove button ${i + 1}`}
                  onClick={() => setButtons((prev) => prev.filter((x) => x.id !== b.id))}
                >
                  <X size={14} className="text-gray-400" />
                </button>
              )}
            </div>
          ))}
          {buttons.length < MAX_BUTTONS && (
            <button type="button" onClick={() => setButtons((prev) => [...prev, emptyButton()])} className="text-xs text-[#008069]">
              + Add button
            </button>
          )}
        </div>
      )}
      {kind === 'list' && (
        <div className="space-y-1">
          <input
            aria-label="List button label"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            placeholder="Button label, e.g. Choose"
            className={`mb-1 ${inputClass}`}
          />
          {rows.map((r, i) => (
            <div key={r.id} className="flex items-center gap-1">
              <input
                aria-label={`Row ${i + 1} title`}
                value={r.title}
                onChange={(e) =>
                  setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, title: e.target.value } : x)))
                }
                placeholder="Title"
                className={inputClass}
              />
              {rows.length > 1 && (
                <button
                  type="button"
                  aria-label={`Remove row ${i + 1}`}
                  onClick={() => setRows((prev) => prev.filter((x) => x.id !== r.id))}
                >
                  <X size={14} className="text-gray-400" />
                </button>
              )}
            </div>
          ))}
          {rows.length < MAX_LIST_ROWS && (
            <button type="button" onClick={() => setRows((prev) => [...prev, emptyRow()])} className="text-xs text-[#008069]">
              + Add row
            </button>
          )}
        </div>
      )}
      {kind === 'cta_url' && (
        <div className="space-y-1">
          <input
            aria-label="Link button label"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            placeholder="Button label, e.g. Track order"
            className={inputClass}
          />
          <input
            aria-label="Link url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className={inputClass}
          />
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          disabled={sending}
          onClick={submit}
          className="rounded-lg bg-[#008069] px-3 py-1.5 text-sm text-white disabled:opacity-50"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default InteractiveComposer;
